/**
 * Pure helpers that summarise ingest activity for the
 * `IngestionDiagnosticsPanel` so users / devs can answer:
 *
 *   - "Was my last scan ingested, or did the backend reject it?"
 *   - "Are box / list scans landing, or only deep (detail) scans?"
 *   - "How many rows are stuck in the review queue?"
 *   - "Which sources have produced the most accepted records?"
 *
 * Data comes from existing endpoints:
 *   - GET /api/dex/batches   → recent batches + stats
 *   - GET /api/dex/records   → current visible records (already cached)
 *   - GET /api/dex/review-queue → rows the OCR couldn't confidently auto-confirm
 *
 * No DOM, no I/O — testable from a plain tsx runner.
 */

export interface BatchLike {
  id?: number;
  source?: string | null;
  rowCount?: number | null;
  surface?: string | null;
  uploadedAt?: number | string | Date | null;
  ingestStatus?: string | null;
  failureReason?: string | null;
}

export interface RecordSourceLike {
  speciesId?: number | null;
  source?: string | null;
  sourceScreen?: string | null;
  deletedAt?: number | string | Date | null;
  hiddenAt?: number | string | Date | null;
}

export interface ReviewQueueItemLike {
  id?: number;
  source?: string | null;
  speciesId?: number | null;
  speciesName?: string | null;
  ocrWarnings?: string | null;
  confidence?: number | null;
  createdAt?: number | string | Date | null;
}

/**
 * "Top-line" scans are the bulk-list and pokedex sources — they ingest
 * many rows per batch. "Deep" scans are single-Pokémon detail captures.
 * Distinguishing the two answers the user's question about whether
 * list/box scans are landing or only deep scans.
 */
const TOP_LINE_SOURCES = new Set([
  "box_scan",
  "pokegenie_list",
  "pokedex_scan",
  "journal_scan",
]);

const DEEP_SOURCES = new Set([
  "detail_ocr",
  "video_frame",
  "profile_ocr",
]);

/**
 * Sources that can carry rows from either tier. `live_scan` is the only
 * one today: the live-scan modal funnels box, detail, catch and pokedex
 * frames through the same screenshot-batch source so the panel needs to
 * treat it as a hybrid tier rather than mis-attributing every live_scan
 * batch to "deep only".
 */
const HYBRID_SOURCES = new Set([
  "live_scan",
]);

const MANUAL_SOURCES = new Set([
  "manual_catch",
  "manual",
  "csv_import",
  "manual_import",
]);

export type IngestTier = "top_line" | "deep" | "hybrid" | "manual" | "other";

export function classifyIngestSource(source: string | null | undefined): IngestTier {
  if (!source) return "other";
  if (TOP_LINE_SOURCES.has(source)) return "top_line";
  if (DEEP_SOURCES.has(source)) return "deep";
  if (HYBRID_SOURCES.has(source)) return "hybrid";
  if (MANUAL_SOURCES.has(source)) return "manual";
  return "other";
}

export interface SourceBucket {
  source: string;
  tier: IngestTier;
  batches: number;
  acceptedRows: number;
  failedBatches: number;
  lastUploadedAt: number | null;
}

export interface IngestionSummary {
  totalBatches: number;
  acceptedBatches: number;
  failedBatches: number;
  totalAcceptedRows: number;
  /** Per-source breakdown, ordered by tier then by lastUploadedAt desc. */
  bySource: SourceBucket[];
  /** True when at least one batch from a top-line source landed successfully. */
  topLineIngested: boolean;
  /** True when at least one batch from a deep source landed successfully. */
  deepIngested: boolean;
  /** Recent failed batches, most-recent first, capped at 10. */
  recentFailures: BatchLike[];
}

function toMillis(v: number | string | Date | null | undefined): number {
  if (v == null) return 0;
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const n = Date.parse(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

export function summarizeBatches(batches: ReadonlyArray<BatchLike> | null | undefined): IngestionSummary {
  const summary: IngestionSummary = {
    totalBatches: 0,
    acceptedBatches: 0,
    failedBatches: 0,
    totalAcceptedRows: 0,
    bySource: [],
    topLineIngested: false,
    deepIngested: false,
    recentFailures: [],
  };
  if (!batches || batches.length === 0) return summary;

  const byKey = new Map<string, SourceBucket>();
  const failures: BatchLike[] = [];
  for (const b of batches) {
    summary.totalBatches += 1;
    const source = (b.source ?? "").toString();
    const tier = classifyIngestSource(source);
    const isFailed = b.ingestStatus === "failed";
    const isComplete = b.ingestStatus === "complete";
    if (isFailed) {
      summary.failedBatches += 1;
      failures.push(b);
    } else if (isComplete) {
      summary.acceptedBatches += 1;
    }
    const rowCount = typeof b.rowCount === "number" && Number.isFinite(b.rowCount) ? b.rowCount : 0;
    if (isComplete) {
      summary.totalAcceptedRows += rowCount;
      if (rowCount > 0) {
        if (tier === "top_line" || tier === "hybrid") {
          summary.topLineIngested = true;
        }
        if (tier === "deep" || tier === "hybrid") {
          summary.deepIngested = true;
        }
      }
    }
    const key = source || "(unknown)";
    let bucket = byKey.get(key);
    if (!bucket) {
      bucket = {
        source: key,
        tier,
        batches: 0,
        acceptedRows: 0,
        failedBatches: 0,
        lastUploadedAt: null,
      };
      byKey.set(key, bucket);
    }
    bucket.batches += 1;
    if (isComplete) bucket.acceptedRows += rowCount;
    if (isFailed) bucket.failedBatches += 1;
    const ts = toMillis(b.uploadedAt);
    if (ts && (bucket.lastUploadedAt === null || ts > bucket.lastUploadedAt)) {
      bucket.lastUploadedAt = ts;
    }
  }

  const tierOrder: Record<IngestTier, number> = {
    top_line: 0,
    hybrid: 1,
    deep: 2,
    manual: 3,
    other: 4,
  };
  summary.bySource = Array.from(byKey.values()).sort((a, b) => {
    if (a.tier !== b.tier) return tierOrder[a.tier] - tierOrder[b.tier];
    return (b.lastUploadedAt ?? 0) - (a.lastUploadedAt ?? 0);
  });
  failures.sort((a, b) => toMillis(b.uploadedAt) - toMillis(a.uploadedAt));
  summary.recentFailures = failures.slice(0, 10);
  return summary;
}

export interface ReviewQueueSummary {
  total: number;
  byReason: Record<string, number>;
  lowConfidenceCount: number;
}

export function summarizeReviewQueue(
  items: ReadonlyArray<ReviewQueueItemLike> | null | undefined,
): ReviewQueueSummary {
  const out: ReviewQueueSummary = {
    total: 0,
    byReason: {},
    lowConfidenceCount: 0,
  };
  if (!items) return out;
  for (const it of items) {
    out.total += 1;
    if (typeof it.confidence === "number" && it.confidence < 0.5) {
      out.lowConfidenceCount += 1;
    }
    if (typeof it.ocrWarnings === "string" && it.ocrWarnings.length > 0) {
      let warnings: unknown = null;
      try { warnings = JSON.parse(it.ocrWarnings); } catch { warnings = null; }
      if (Array.isArray(warnings)) {
        for (const w of warnings) {
          if (typeof w === "string" && w.length > 0) {
            out.byReason[w] = (out.byReason[w] ?? 0) + 1;
          }
        }
      } else {
        out.byReason[it.ocrWarnings] = (out.byReason[it.ocrWarnings] ?? 0) + 1;
      }
    }
  }
  return out;
}

/**
 * Combined "is the trainer's ingest healthy?" verdict that the panel
 * surfaces as a one-line status. This is the question the user asked
 * for: are top-line list/box scans landing, or only deep scans?
 */
export type IngestHealth = "ok" | "deep_only" | "top_line_only" | "stalled" | "no_data";

export function deriveIngestHealth(summary: IngestionSummary): IngestHealth {
  if (summary.totalBatches === 0) return "no_data";
  if (summary.acceptedBatches === 0) return "stalled";
  if (summary.topLineIngested && summary.deepIngested) return "ok";
  if (summary.deepIngested) return "deep_only";
  if (summary.topLineIngested) return "top_line_only";
  return "stalled";
}

export function describeHealth(h: IngestHealth): string {
  switch (h) {
    case "ok": return "Top-line and deep scans are both landing.";
    case "deep_only": return "Only deep (detail) scans have been accepted recently. Top-line Dex scans are not reaching the backend.";
    case "top_line_only": return "Only top-line Dex scans have been accepted. Deep detail scans are not landing.";
    case "stalled": return "No batches have been accepted yet. Check the recent failure list.";
    case "no_data": return "No scan batches recorded for this account yet.";
  }
}
